(() => {
  const CLOSE_DELAY = 150; // ms grace period to move the pointer from the nav item into the panel
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  let openTrigger = null;
  let closeTimer = null;

  // Top nav items (snippets/mega-menu-left|right.liquid) that open a panel
  const panelFor = (trigger) => document.getElementById(trigger.dataset.megaTrigger);
  // Keyboard / screen reader toggle (snippets/mega-menu-toggle.liquid), which carries aria-expanded
  const toggleFor = (trigger) => trigger.querySelector('.mega-toggle');
  const header = () => document.getElementById('site-header');

  // Focusable elements actually shown in a panel (hidden categories are skipped)
  const focusablesIn = (panel) =>
    Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);

  // Sidebar: hovering or clicking (incl. Enter / Space) a category shows its cards
  const activateCategory = (panel, cat) => {
    panel.querySelectorAll('.mega-cat').forEach((c) => {
      const on = c === cat;
      c.classList.toggle('is-active', on);
      c.setAttribute('aria-current', on ? 'true' : 'false');
      const content = document.getElementById(c.getAttribute('aria-controls'));
      if (content) content.hidden = !on;
    });
  };

  const cancelClose = () => {
    clearTimeout(closeTimer);
    closeTimer = null;
  };

  const closePanel = () => {
    cancelClose();
    if (!openTrigger) return;
    const panel = panelFor(openTrigger);
    if (panel) panel.hidden = true;
    openTrigger.classList.remove('is-mega-active');
    toggleFor(openTrigger)?.setAttribute('aria-expanded', 'false');
    header()?.classList.remove('mega-is-open');
    openTrigger = null;
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer = setTimeout(closePanel, CLOSE_DELAY);
  };

  // Panel images are loading="lazy" so they don't slow the page, but lazy images inside hidden
  // panels only start loading once a panel opens, which leaves blank tiles on first open.
  // Switch them to eager just ahead of use: when the visitor reaches the header, or once the
  // page has loaded and the browser is idle. Skipped while the panels are hidden (mobile).
  const preloadPanelImages = () => {
    const panels = document.querySelector('.mega-panels');
    if (!panels || getComputedStyle(panels).display === 'none') return;
    panels.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  };

  const openPanel = (trigger) => {
    preloadPanelImages();
    cancelClose();
    if (openTrigger === trigger) return;
    closePanel();
    const panel = panelFor(trigger);
    if (!panel) return;
    const firstCat = panel.querySelector('.mega-cat');
    if (firstCat) activateCategory(panel, firstCat);
    panel.hidden = false;
    trigger.classList.add('is-mega-active');
    toggleFor(trigger)?.setAttribute('aria-expanded', 'true');
    header()?.classList.add('mega-is-open');
    openTrigger = trigger;
  };

  // After a panel closes from the keyboard, carry on to whatever follows its nav item
  const focusAfter = (trigger) => {
    const all = Array.from(document.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    const last = toggleFor(trigger) || trigger;
    const next = all[all.indexOf(last) + 1];
    if (next) next.focus();
  };

  const init = () => {
    closePanel();

    const siteHeader = header();
    if (siteHeader) {
      siteHeader.addEventListener('pointerenter', preloadPanelImages, { once: true });
      siteHeader.addEventListener('focusin', preloadPanelImages, { once: true });
    }

    document.querySelectorAll('[data-mega-trigger]').forEach((trigger) => {
      const toggle = toggleFor(trigger);

      trigger.addEventListener('mouseenter', () => openPanel(trigger));
      trigger.addEventListener('mouseleave', scheduleClose);

      // Touch screens have no hover: the first tap opens the panel, the next tap follows the link.
      // Keyboard Enter on the link (event.detail === 0) just follows it; the toggle opens the panel.
      trigger.addEventListener('click', (event) => {
        if (event.detail === 0 || event.target.closest('.mega-toggle')) return;
        if (openTrigger !== trigger) {
          event.preventDefault();
          openPanel(trigger);
        }
      });

      if (!toggle) return;

      // Enter / Space (native button) open or close the panel
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        if (openTrigger === trigger) closePanel();
        else openPanel(trigger);
      });

      // Tab from the open toggle goes into its panel
      toggle.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || event.shiftKey || openTrigger !== trigger) return;
        const first = focusablesIn(panelFor(trigger))[0];
        if (!first) return;
        event.preventDefault();
        first.focus();
      });
    });

    document.querySelectorAll('.mega-panel').forEach((panel) => {
      panel.addEventListener('mouseenter', cancelClose);
      panel.addEventListener('mouseleave', scheduleClose);

      panel.querySelectorAll('.mega-cat').forEach((cat) => {
        ['mouseenter', 'click'].forEach((type) => {
          cat.addEventListener(type, () => activateCategory(panel, cat));
        });
      });

      // Tab past the end closes the panel and moves on along the nav; Shift+Tab from the start
      // goes back to the toggle. The panels sit at the end of the header in the page code, so
      // this keeps them in the visual tab order.
      panel.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || !openTrigger || panelFor(openTrigger) !== panel) return;
        const items = focusablesIn(panel);
        if (!items.length) return;
        const trigger = openTrigger;

        if (!event.shiftKey && document.activeElement === items[items.length - 1]) {
          event.preventDefault();
          closePanel();
          focusAfter(trigger);
        } else if (event.shiftKey && document.activeElement === items[0]) {
          event.preventDefault();
          (toggleFor(trigger) || trigger).focus();
        }
      });
    });
  };

  // Close when focus or a tap moves outside the open item and its panel
  const isInsideOpenMenu = (node) => {
    if (!openTrigger || !node) return false;
    const panel = panelFor(openTrigger);
    return openTrigger.contains(node) || (panel && panel.contains(node));
  };

  document.addEventListener('focusin', (event) => {
    if (openTrigger && !isInsideOpenMenu(event.target)) closePanel();
  });

  document.addEventListener('click', (event) => {
    if (openTrigger && !isInsideOpenMenu(event.target)) closePanel();
  });

  // Escape closes from anywhere (nav item, toggle or inside the panel) and returns focus to the toggle
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openTrigger) return;
    const trigger = openTrigger;
    const focusWasInMenu = isInsideOpenMenu(document.activeElement);
    closePanel();
    if (focusWasInMenu) (toggleFor(trigger) || trigger).focus();
  });

  // Theme editor: re-bind after the header re-renders, and open a block's panel when selected
  document.addEventListener('shopify:section:load', init);

  document.addEventListener('shopify:block:select', (event) => {
    if (!event.target.classList || !event.target.classList.contains('mega-panel')) return;
    const trigger = document.querySelector(`[data-mega-trigger="${event.target.id}"]`);
    if (trigger) openPanel(trigger);
  });

  document.addEventListener('shopify:block:deselect', closePanel);

  init();

  // Warm the panel images once the page itself has finished loading
  const preloadWhenIdle = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(preloadPanelImages, { timeout: 3000 });
    else setTimeout(preloadPanelImages, 1500);
  };
  if (document.readyState === 'complete') preloadWhenIdle();
  else window.addEventListener('load', preloadWhenIdle, { once: true });
})();
